import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { CoreUIFlashToastConfig, CoreUIFlashToastPosition, CoreUIFlashToastType } from './flash-toast.model';
import { CoreUIFlashToastService } from './flash-toast.service';

type CoreUIFlashToastPhase = 'compact' | 'visible' | 'closing' | 'leaving';

@Component({
  selector: 'core-ui-flash-toast',
  templateUrl: './flash-toast.component.html',
  styleUrls: ['./flash-toast.component.scss'],
  standalone: false,
})
export class CoreUIFlashToastComponent implements OnInit, OnDestroy {
  config: CoreUIFlashToastConfig | null = null;
  phase: CoreUIFlashToastPhase = 'compact';

  private subscription?: Subscription;
  private compactTimeout?: ReturnType<typeof setTimeout>;
  private durationTimeout?: ReturnType<typeof setTimeout>;
  private closingTimeout?: ReturnType<typeof setTimeout>;
  private leavingTimeout?: ReturnType<typeof setTimeout>;

  private readonly compactDurationMs = 680;
  private readonly expandDurationMs = 560;
  private readonly collapseDurationMs = 640;
  private readonly exitDurationMs = 840;

  constructor(private flashToast: CoreUIFlashToastService) {}

  ngOnInit(): void {
    this.subscription = this.flashToast.toasts$.subscribe(config => {
      this.clearTimeouts();
      this.config = config;
      this.phase = 'compact';

      this.compactTimeout = setTimeout(() => {
        this.phase = 'visible';

        this.durationTimeout = setTimeout(() => {
          this.close();
        }, this.expandDurationMs + (config.duration ?? 2500));
      }, this.compactDurationMs);
    });
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    this.subscription?.unsubscribe();
  }

  get position(): CoreUIFlashToastPosition {
    return this.config?.position ?? 'top-center';
  }

  get type(): CoreUIFlashToastType {
    return this.config?.type ?? 'info';
  }

  get iconName(): string {
    if (this.config?.iconName) return this.config.iconName;

    const iconByType: Record<CoreUIFlashToastType, string> = {
      success: 'icon_check',
      error: 'icon_close-red',
      warning: 'icon_warning',
      info: 'icon_info',
      delete: 'icon_delete',
    };

    return iconByType[this.type];
  }

  get phaseClass(): string {
    return `is-${this.phase}`;
  }

  private close(): void {
    this.phase = 'closing';

    this.closingTimeout = setTimeout(() => {
      this.phase = 'leaving';

      this.leavingTimeout = setTimeout(() => {
        this.config = null;
        this.phase = 'compact';
      }, this.exitDurationMs);
    }, this.collapseDurationMs);
  }

  private clearTimeouts(): void {
    clearTimeout(this.compactTimeout);
    clearTimeout(this.durationTimeout);
    clearTimeout(this.closingTimeout);
    clearTimeout(this.leavingTimeout);

    this.compactTimeout = undefined;
    this.durationTimeout = undefined;
    this.closingTimeout = undefined;
    this.leavingTimeout = undefined;
  }
}
